// Suggested routes for each destination: the order to visit places in, how long
// to stay, and how you actually get between them.
//
// Deliberately no prices. Fares and room rates go stale within a season and we
// will not invent them. What does not go stale is the shape of a trip: which
// direction to travel, how long a leg really takes, and the mistake that costs
// people days. That is what this file holds.

export const routes = {
  'dubai': {
    id: 'dubai',
    name: 'Dubai',
    shape: 'One base, four very different districts',
    nights: 5,
    lead: 'Dubai is compact enough that you never need to change hotel, but the city reads completely differently depending on which side of the creek you are standing on. Do the old city before the new one and the skyline means more.',
    stops: [
      {
        name: 'Deira and Al Fahidi',
        nights: 1,
        note: 'Start where the city started. The gold and spice souks, the wind-tower houses of Al Fahidi, and a one-dirham abra across the creek.',
        onward: { mode: 'Metro', time: '25 min', detail: 'Red Line from Al Ras to Burj Khalifa/Dubai Mall' },
      },
      {
        name: 'Downtown',
        nights: 2,
        note: 'Burj Khalifa, Dubai Mall and the fountain. Book the observation deck for the last slot before sunset so you see the city in daylight and lit up.',
        onward: { mode: 'Metro or taxi', time: '30 min', detail: 'Red Line to Dubai Marina' },
      },
      {
        name: 'Marina and the Palm',
        nights: 2,
        note: 'Beaches, the marina walk and the monorail out along Palm Jumeirah. This is where the days slow down.',
        onward: { mode: 'Road', time: '45 min', detail: 'Pickup from your hotel for the desert' },
      },
      {
        name: 'Desert',
        nights: 0,
        note: 'An evening trip rather than a stay. Dune drive, sunset, dinner under the sky, back in the city by eleven.',
      },
    ],
    shorter: 'On three nights, drop the Marina and keep Old Dubai. The souks and the abra are the part you cannot see anywhere else.',
    longer: 'Add a day trip to Abu Dhabi for the Sheikh Zayed Grand Mosque, about ninety minutes each way by road.',
    mistake: 'Going in July or August. Summer runs to 45°C and everything outdoors closes down to a dash between air-conditioned buildings. November to March is a genuinely different city.',
  },

  'thailand': {
    id: 'thailand',
    name: 'Thailand',
    shape: 'North to south, one way',
    nights: 12,
    lead: 'The classic Thailand route runs top to bottom: city, then mountains, then islands. Travel it in that order and each stage feels like a reward for the last. Fly out of the south rather than doubling back to Bangkok.',
    stops: [
      {
        name: 'Bangkok',
        nights: 3,
        note: 'The Grand Palace and Wat Pho first thing in the morning, before the heat and the tour groups. Save an evening for the river.',
        onward: { mode: 'Overnight train', time: '12–13 hr', detail: 'Second-class sleeper, lower berth if you can get it. A flight does it in 1 hr 15.' },
      },
      {
        name: 'Chiang Mai',
        nights: 3,
        note: 'Old city temples inside the moat, a cooking class, and an ethical elephant sanctuary — one that does not offer rides.',
        onward: { mode: 'Flight', time: '2 hr', detail: 'Direct to Krabi. Overland is possible but costs you two days.' },
      },
      {
        name: 'Krabi and Ao Nang',
        nights: 3,
        note: 'Railay by longtail boat, limestone cliffs straight out of the sea, and the four-island trip if the weather holds.',
        onward: { mode: 'Ferry', time: '1 hr 45', detail: 'To Koh Phi Phi. Koh Lanta is the quieter alternative at about the same distance.' },
      },
      {
        name: 'Koh Phi Phi or Koh Lanta',
        nights: 3,
        note: 'Phi Phi if you want the viewpoint and the nightlife, Lanta if you want long empty beaches and an early night.',
        onward: { mode: 'Ferry then flight', time: '3 hr total', detail: 'Back to Krabi, then 1 hr 20 to Bangkok for your flight home' },
      },
    ],
    shorter: 'On seven nights, cut Chiang Mai. Bangkok and the Andaman coast still make a complete trip; the north deserves more than a rushed two nights.',
    longer: 'Add Chiang Rai and the white temple, three hours north of Chiang Mai, or slow down and give Koh Lanta a full week.',
    mistake: 'Trying to fit both coasts into one trip. The Gulf islands — Samui, Phangan, Tao — and the Andaman islands — Phuket, Krabi, Phi Phi — sit on opposite sides of the country with opposite monsoon seasons. When one coast is at its best the other is being rained on. Pick the coast that matches your dates.',
  },

  'vietnam': {
    id: 'vietnam',
    name: 'Vietnam',
    shape: 'North to south, open jaw',
    nights: 14,
    lead: 'Vietnam is long and thin, which makes the route obvious and the ticketing less so. Fly into Hanoi and out of Ho Chi Minh City on an open-jaw booking so you never retrace a single kilometre.',
    stops: [
      {
        name: 'Hanoi',
        nights: 3,
        note: 'The Old Quarter on foot, egg coffee, and the water puppets. Cross roads slowly and without stopping — the traffic flows around you.',
        onward: { mode: 'Road', time: '2 hr 30', detail: 'Expressway to the coast for your cruise' },
      },
      {
        name: 'Lan Ha or Ha Long Bay',
        nights: 2,
        note: 'One night on the water is enough to see it; two lets you get past the day-trip route. Lan Ha Bay is the same limestone with a fraction of the boats.',
        onward: { mode: 'Road', time: '3 hr 30', detail: 'Back past Hanoi and south to Ninh Binh' },
      },
      {
        name: 'Ninh Binh',
        nights: 2,
        note: 'Trang An by rowing boat through flooded caves, and the climb up Mua Cave for the view back over the whole valley.',
        onward: { mode: 'Road then flight', time: '3 hr 30 total', detail: 'To Hanoi airport, then 1 hr 20 to Da Nang' },
      },
      {
        name: 'Hoi An',
        nights: 4,
        note: 'The lantern-lit old town, a tailor if you want one, and An Bang beach fifteen minutes away when you have had enough of the crowds.',
        onward: { mode: 'Flight', time: '1 hr 20', detail: 'Da Nang to Ho Chi Minh City' },
      },
      {
        name: 'Ho Chi Minh City',
        nights: 3,
        note: 'The War Remnants Museum, the Reunification Palace, and street food in District 4. Louder and faster than Hanoi, and it knows it.',
      },
    ],
    shorter: 'On ten nights, drop Ninh Binh and give the time to Hoi An. On seven, pick either the north or the south — not both.',
    longer: 'Add Hue between Ninh Binh and Hoi An for the citadel and the tombs, or Sapa in the far north if you are there between September and November.',
    mistake: 'Booking a return flight into and out of the same city. A Hanoi-in, Saigon-out ticket usually costs about the same and saves you a wasted internal flight plus a day. Ask for it as a multi-city booking.',
  },

  'sri-lanka': {
    id: 'sri-lanka',
    name: 'Sri Lanka',
    shape: 'Clockwise loop from Colombo',
    nights: 12,
    lead: 'A loop that takes you through ruins, hill country, tea and then the coast, in the order that makes the beach feel earned. Almost everyone travels it clockwise, and there is a good reason: you finish at sea level with nothing left to do.',
    stops: [
      {
        name: 'Sigiriya and Dambulla',
        nights: 2,
        note: 'Climb Sigiriya at first light. The cave temples at Dambulla and the ruined city of Polonnaruwa are both within an easy drive.',
        onward: { mode: 'Road', time: '3 hr', detail: 'South into the hills to Kandy' },
      },
      {
        name: 'Kandy',
        nights: 2,
        note: 'The Temple of the Tooth at evening puja, and the botanical gardens at Peradeniya. A good place to slow down before the hill country.',
        onward: { mode: 'Train', time: '6–7 hr', detail: 'The Kandy to Ella line. Book a reserved seat weeks ahead, sit on the right.' },
      },
      {
        name: 'Ella',
        nights: 2,
        note: 'Little Adam’s Peak at sunrise, the Nine Arches Bridge, and a tea factory tour that is more interesting than it sounds.',
        onward: { mode: 'Road', time: '2 hr 30', detail: 'Down out of the hills to the national park' },
      },
      {
        name: 'Udawalawe or Yala',
        nights: 1,
        note: 'Udawalawe for near-guaranteed elephants and far fewer jeeps; Yala if leopards are the reason you came.',
        onward: { mode: 'Road', time: '2 hr', detail: 'South to the coast' },
      },
      {
        name: 'Mirissa and the south coast',
        nights: 4,
        note: 'Beaches, whale watching between December and April, and the Dutch fort at Galle an hour along the coast.',
        onward: { mode: 'Expressway', time: '2 hr', detail: 'Straight back to Colombo airport' },
      },
    ],
    shorter: 'On eight nights, drop the safari and one hill-country night. Do not drop the train.',
    longer: 'Add Anuradhapura before Sigiriya for the oldest ruins on the island, or Arugam Bay on the east coast between May and September.',
    mistake: 'Reading distances off a map. Sri Lanka looks small and the hill roads are slow, winding and often behind a bus — a hundred kilometres can take four hours. Plan by driving time, never by distance, and do not schedule two long transfers back to back.',
  },

  'indonesia': {
    id: 'indonesia',
    name: 'Bali and Nusa Penida',
    shape: 'Coast, inland, then islands',
    nights: 12,
    lead: 'Bali is not one place and the parts of it are genuinely different. Split your stay rather than picking one base and driving — the island is small but the traffic is not.',
    stops: [
      {
        name: 'Canggu or Seminyak',
        nights: 3,
        note: 'Surf breaks, beach clubs and the best coffee on the island. A soft landing while the jet lag wears off.',
        onward: { mode: 'Road', time: '1 hr 30', detail: 'North and inland to Ubud' },
      },
      {
        name: 'Ubud',
        nights: 4,
        note: 'Rice terraces, the monkey forest, and temples at Tirta Empul. Get out to the Campuhan ridge before breakfast while it is still empty.',
        onward: { mode: 'Road then fast boat', time: '2 hr total', detail: 'To Sanur, then 45 min across to Nusa Penida' },
      },
      {
        name: 'Nusa Penida',
        nights: 2,
        note: 'Kelingking, Angel’s Billabong and Diamond Beach. The roads are rough and the cliffs are unfenced — hire a driver rather than a scooter.',
        onward: { mode: 'Fast boat', time: '1 hr 30', detail: 'Direct to the Gili Islands, or back via Sanur' },
      },
      {
        name: 'Gili Islands or Amed',
        nights: 3,
        note: 'Gili Air for snorkelling with turtles and no motor traffic at all; Amed on Bali’s east coast for diving and black-sand quiet.',
      },
    ],
    shorter: 'On eight nights, drop the Gilis. Ubud and Penida are the two that people remember.',
    longer: 'Add Munduk or Sidemen for two nights of mountains and no beach clubs whatsoever, or cross to Lombok for Rinjani.',
    mistake: 'Booking Ubud and expecting a beach. Ubud is inland, up in the rice fields, and the nearest coast is well over an hour away through traffic. It is the best base on the island — just not for swimming.',
  },

  'malaysia': {
    id: 'malaysia',
    name: 'Malaysia',
    shape: 'Up the peninsula, west coast',
    nights: 12,
    lead: 'Peninsular Malaysia strings together neatly from south to north — city, heritage town, highlands, island — with short flights and good roads between. Borneo is a separate trip.',
    stops: [
      {
        name: 'Kuala Lumpur',
        nights: 3,
        note: 'The Petronas Towers, Batu Caves, and hawker food in Jalan Alor. The metro covers more of the city than you expect.',
        onward: { mode: 'Flight', time: '1 hr', detail: 'To Penang. The train takes about four hours and is a pleasant alternative.' },
      },
      {
        name: 'George Town, Penang',
        nights: 3,
        note: 'Shophouses, street art and arguably the best food in Southeast Asia. The clan jetties at sunset are worth the walk.',
        onward: { mode: 'Road', time: '4 hr', detail: 'Inland and up into the hills' },
      },
      {
        name: 'Cameron Highlands',
        nights: 2,
        note: 'Tea plantations, mossy forest and the first cool air of the trip. Bring a layer — it drops to the mid teens at night.',
        onward: { mode: 'Road then ferry, or flight', time: '5 hr or 1 hr', detail: 'Back to Penang for the ferry, or fly from KL' },
      },
      {
        name: 'Langkawi',
        nights: 4,
        note: 'Beaches, the SkyBridge cable car, and mangrove tours. The quiet end of the trip.',
      },
    ],
    shorter: 'On eight nights, drop the Cameron Highlands. It is the longest drive for the smallest payoff if you are short of time.',
    longer: 'Add Malacca for two nights south of KL, or give Borneo its own week for Sepilok, Kinabatangan and Mount Kinabalu.',
    mistake: 'Trying to combine the peninsula and Borneo in under two weeks. They are separated by a two-and-a-half-hour flight over the South China Sea, and Borneo needs at least five nights to be worth the trip. Choose one.',
  },

  'singapore': {
    id: 'singapore',
    name: 'Singapore',
    shape: 'One base, neighbourhood by neighbourhood',
    nights: 4,
    lead: 'Singapore is small enough to stay in one hotel and see all of it. The route here is not about moving bags, it is about which order to walk the neighbourhoods so the city makes sense.',
    stops: [
      {
        name: 'Marina Bay',
        nights: 0,
        note: 'Gardens by the Bay, the Supertree light show after dark, and the ArtScience Museum. Start here to get the skyline fixed in your head.',
        onward: { mode: 'MRT', time: '10 min', detail: 'Downtown Line to Chinatown' },
      },
      {
        name: 'Chinatown and Tiong Bahru',
        nights: 0,
        note: 'Temples, the Maxwell hawker centre, then the low-rise art deco streets and independent bookshops of Tiong Bahru.',
        onward: { mode: 'MRT', time: '15 min', detail: 'To Bugis for Kampong Glam' },
      },
      {
        name: 'Kampong Glam and Little India',
        nights: 0,
        note: 'The Sultan Mosque, the shophouses of Haji Lane, and the noise and colour of Tekka Centre a short walk north.',
        onward: { mode: 'MRT and monorail', time: '25 min', detail: 'To Sentosa' },
      },
      {
        name: 'Sentosa and the Southern Ridges',
        nights: 0,
        note: 'Beaches and theme parks on the island, or the canopy walk from Mount Faber if you would rather be in the trees.',
        onward: { mode: 'Bus or MRT', time: '30 min', detail: 'East to Katong' },
      },
      {
        name: 'Katong and the East Coast',
        nights: 0,
        note: 'Peranakan shophouses, laksa that people argue about, and a long flat stretch of park along the sea.',
      },
    ],
    shorter: 'On a single day between flights, do Marina Bay and Chinatown and nothing else. Both are minutes from the airport line.',
    longer: 'Add the Botanic Gardens, the zoo’s night safari, and a day across the causeway to Johor Bahru.',
    mistake: 'Treating Singapore as a one-day stopover. It is expensive per night, which pushes people to rush it, and the result is a photo of the Marina Bay Sands and nothing else. Three nights is the point at which the neighbourhoods start to separate out.',
  },

  'cambodia': {
    id: 'cambodia',
    name: 'Cambodia',
    shape: 'North to south, overland',
    nights: 11,
    lead: 'Most people come for Angkor and leave after three nights. The country south of it is quieter, cheaper and almost empty of visitors, and the roads have improved enough to make the overland run genuinely pleasant.',
    stops: [
      {
        name: 'Siem Reap',
        nights: 4,
        note: 'Buy the three-day Angkor pass. Sunrise at Angkor Wat once, then Bayon and Ta Prohm, then the outer temples like Banteay Srei on the third day.',
        onward: { mode: 'Road', time: '3 hr', detail: 'South-west to Battambang' },
      },
      {
        name: 'Battambang',
        nights: 2,
        note: 'French colonial streets, the bamboo train, and the bat cave at Phnom Sampeau at dusk — a column of bats that takes half an hour to leave.',
        onward: { mode: 'Road', time: '5 hr', detail: 'South-east to the capital' },
      },
      {
        name: 'Phnom Penh',
        nights: 2,
        note: 'Tuol Sleng and Choeung Ek take a full and very heavy day. The riverfront and the Royal Palace are the counterweight.',
        onward: { mode: 'Road', time: '3 hr', detail: 'South to the coast' },
      },
      {
        name: 'Kampot and Kep',
        nights: 3,
        note: 'Pepper farms, a river that catches the sunset, and crab at the Kep market. The most relaxed corner of the country.',
      },
    ],
    shorter: 'On seven nights, do Siem Reap and Kampot and skip the middle. On five, Angkor alone.',
    longer: 'Add Koh Rong or Koh Rong Sanloem for three nights of white sand off the south coast, reached by fast boat from Sihanoukville.',
    mistake: 'Giving Angkor one day. It is not a single temple, it is a city the size of a small county — Angkor Wat, Angkor Thom and Ta Prohm alone are spread over fifteen kilometres. A one-day pass means a hot, rushed blur. Three days lets you go at dawn and rest through the afternoon heat.',
  },

  'laos': {
    id: 'laos',
    name: 'Laos',
    shape: 'North to south along the new railway',
    nights: 11,
    lead: 'The China–Laos railway has quietly rewritten travel here. Journeys that used to be six or seven hours of mountain switchbacks are now under an hour, which makes a route that was once punishing genuinely easy.',
    stops: [
      {
        name: 'Luang Prabang',
        nights: 4,
        note: 'The alms-giving at dawn — watch from a distance and do not photograph it up close. Kuang Si waterfall, the night market, and Mount Phousi at sunset.',
        onward: { mode: 'Train', time: '1 hr', detail: 'The new line to Vang Vieng. The road takes six hours or more.' },
      },
      {
        name: 'Vang Vieng',
        nights: 2,
        note: 'Karst mountains, lagoons and hot-air balloons at sunrise. Much calmer than its old reputation suggests.',
        onward: { mode: 'Train', time: '1 hr', detail: 'South to the capital' },
      },
      {
        name: 'Vientiane',
        nights: 2,
        note: 'A capital that feels like a large town. Pha That Luang, the riverside night market, and the best French bakeries in the region.',
        onward: { mode: 'Flight', time: '1 hr 20', detail: 'South to Pakse for the 4000 Islands' },
      },
      {
        name: 'Si Phan Don, the 4000 Islands',
        nights: 3,
        note: 'Where the Mekong spreads out and stops being in a hurry. Hammocks, bicycles, and Irrawaddy dolphins if you are lucky.',
      },
    ],
    shorter: 'On seven nights, stay north: Luang Prabang and Vang Vieng, with Nong Khiaw instead of the south.',
    longer: 'Add Nong Khiaw and Muang Ngoi north of Luang Prabang for river villages reached only by boat, or the Thakhek loop by motorbike.',
    mistake: 'Booking the two-day slow boat from Huay Xai without realising it is two full days on a wooden bench, with an overnight stop in Pakbeng in the middle. It is a wonderful trip if you want it and a miserable surprise if you do not.',
  },

  'philippines': {
    id: 'philippines',
    name: 'Philippines',
    shape: 'Two island groups, linked by air',
    nights: 14,
    lead: 'Over seven thousand islands means the route is really a question of which two or three you commit to. Fly between island groups and take boats only within them — that single rule saves days.',
    stops: [
      {
        name: 'Manila',
        nights: 1,
        note: 'Mostly a transit stop. Intramuros is worth the morning if your connection allows it.',
        onward: { mode: 'Flight', time: '1 hr 20', detail: 'To El Nido direct, or to Puerto Princesa and 5 hr by van' },
      },
      {
        name: 'El Nido, Palawan',
        nights: 4,
        note: 'Island-hopping tours A through D around Bacuit Bay. Tour A for the lagoons, Tour C for the beaches and better snorkelling.',
        onward: { mode: 'Fast ferry', time: '3 hr 30', detail: 'North to Coron. Cancelled in rough weather — keep a spare day.' },
      },
      {
        name: 'Coron',
        nights: 3,
        note: 'Wreck diving over a sunken Japanese fleet, Kayangan Lake, and hot springs. Quieter and more dramatic than El Nido.',
        onward: { mode: 'Flight', time: '1 hr 30', detail: 'Via Manila or Cebu' },
      },
      {
        name: 'Bohol or Siargao',
        nights: 5,
        note: 'Bohol for the Chocolate Hills, tarsiers and Panglao’s beaches; Siargao for surf and a slower, sandier pace.',
      },
    ],
    shorter: 'On nine nights, do Palawan alone — El Nido and Coron together are a complete trip.',
    longer: 'Add Siquijor or Malapascua from Cebu, or Batanes in the far north if you want somewhere almost no one goes.',
    mistake: 'Trying to link distant islands by ferry to save money. Sea routes between island groups can take twenty hours or run twice a week, and a cancelled sailing strands you. Domestic flights are frequent and cheap, and they buy back whole days of your trip.',
  },

  'maldives': {
    id: 'maldives',
    name: 'Maldives',
    shape: 'Local islands, hopped by ferry',
    nights: 9,
    lead: 'You do not have to book a resort. Guesthouses on inhabited local islands put you on the same water for a fraction of the cost, and the ferry network between them is the trip.',
    stops: [
      {
        name: 'Malé',
        nights: 1,
        note: 'One night to catch the rhythm of the country and the fish market. Most transfers leave from here anyway.',
        onward: { mode: 'Speedboat or public ferry', time: '30 min or 2 hr', detail: 'To Maafushi' },
      },
      {
        name: 'Maafushi',
        nights: 3,
        note: 'The most developed local island: dive shops, sandbank trips and a designated bikini beach. A good introduction.',
        onward: { mode: 'Speedboat', time: '2 hr 30', detail: 'South to Dhigurah, via Malé or direct' },
      },
      {
        name: 'Dhigurah',
        nights: 3,
        note: 'A three-kilometre sand spit in South Ari Atoll, where whale sharks are in the water all year. The main reason to come this far.',
        onward: { mode: 'Ferry', time: '1 hr', detail: 'To a neighbouring island in the same atoll' },
      },
      {
        name: 'Fulidhoo or a resort night',
        nights: 2,
        note: 'Fulidhoo is tiny, quiet and full of stingrays at dusk. Or split the difference and finish with one night on a resort island.',
      },
    ],
    shorter: 'On six nights, Maafushi and Dhigurah alone. Do not try to add a third island.',
    longer: 'Add a two-night liveaboard dive boat, which reaches atolls the ferries do not serve.',
    mistake: 'Planning around the public ferries without checking the timetable. They are cheap and slow, they do not run on Fridays, and many routes go only two or three days a week. Getting the days wrong can cost you two nights in Malé waiting for a boat.',
  },

  'seychelles': {
    id: 'seychelles',
    name: 'Seychelles',
    shape: 'Three islands, west to east',
    nights: 10,
    lead: 'Three main islands, each with a completely different character, linked by a ferry that runs several times a day. There is no reason to stay on just one.',
    stops: [
      {
        name: 'Mahé',
        nights: 3,
        note: 'The biggest island and the only real town. Beau Vallon for swimming, Anse Major on foot, and the market at Victoria.',
        onward: { mode: 'Cat Cocos ferry', time: '1 hr', detail: 'To Praslin. A 15-minute flight is the alternative.' },
      },
      {
        name: 'Praslin',
        nights: 3,
        note: 'The Vallée de Mai, where the coco de mer grows and nowhere else on earth. Anse Lazio is the beach on the postcards.',
        onward: { mode: 'Ferry', time: '15 min', detail: 'Across to La Digue' },
      },
      {
        name: 'La Digue',
        nights: 4,
        note: 'Bicycles instead of cars, granite boulders at Anse Source d’Argent, and the walk over the hill to Grand Anse.',
      },
    ],
    shorter: 'On six nights, Praslin and La Digue. Mahé is the one to cut if you have to cut something.',
    longer: 'Add Bird Island or Silhouette for two nights, or a live-aboard sailing charter through the inner islands.',
    mistake: 'Day-tripping La Digue from Praslin. It is the island people fall for, and a day trip gives you four hours of it in the middle of the day, sharing the best beach with every other day tripper. Stay the night and have the place at dawn.',
  },

  'mauritius': {
    id: 'mauritius',
    name: 'Mauritius',
    shape: 'Around the coast, three bases',
    nights: 9,
    lead: 'Mauritius is small enough to drive across in ninety minutes, which convinces most people to book one hotel and day-trip everything. Moving base twice costs you almost nothing and changes the trip completely.',
    stops: [
      {
        name: 'North — Grand Baie',
        nights: 3,
        note: 'The liveliest coast. Boat trips to the northern islets, the botanical gardens at Pamplemousses, and Port Louis market half an hour south.',
        onward: { mode: 'Road', time: '1 hr', detail: 'Down the east coast' },
      },
      {
        name: 'East — Belle Mare',
        nights: 2,
        note: 'Long empty beaches and the best lagoon on the island. Île aux Cerfs is a boat ride offshore.',
        onward: { mode: 'Road', time: '1 hr 30', detail: 'Across the south to Le Morne' },
      },
      {
        name: 'South west — Le Morne and Chamarel',
        nights: 4,
        note: 'The mountain at Le Morne, kitesurfing in the lagoon below it, and inland to the seven coloured earths, the waterfall and Black River Gorges.',
      },
    ],
    shorter: 'On six nights, north and south west. The east is the easiest to drop.',
    longer: 'Add two nights on Rodrigues, a ninety-minute flight east and about forty years behind the main island.',
    mistake: 'Booking one resort for the whole stay and expecting to see the island from it. Distances are short but the roads are single carriageway and slow, so a day trip to the other end burns three hours in the car. Two moves, and everything is within twenty minutes.',
  },

  'turkey': {
    id: 'turkey',
    name: 'Turkey',
    shape: 'Istanbul, the interior, then the coast',
    nights: 14,
    lead: 'A loop that goes city, then landscape, then ruins, then sea, and flies home from the far end. Internal flights are frequent and short, which is what makes a country this size manageable in two weeks.',
    stops: [
      {
        name: 'Istanbul',
        nights: 4,
        note: 'Hagia Sophia, the Blue Mosque and Topkapı in Sultanahmet, then cross to Kadıköy on the Asian side for the food and the ferry ride.',
        onward: { mode: 'Flight', time: '1 hr 20', detail: 'To Kayseri or Nevşehir for Cappadocia' },
      },
      {
        name: 'Cappadocia',
        nights: 3,
        note: 'Balloons at sunrise, the underground city at Derinkuyu, and the Göreme open-air museum. Stay in a cave hotel — it is not a gimmick.',
        onward: { mode: 'Flight', time: '1 hr 30', detail: 'To Antalya, via Istanbul on some days' },
      },
      {
        name: 'Kaş or Antalya',
        nights: 3,
        note: 'The Turquoise Coast. Kaş for a small harbour town and sea kayaking over a sunken city; Antalya for the old town and easier flights.',
        onward: { mode: 'Road', time: '3 hr 30', detail: 'Inland and north to Pamukkale' },
      },
      {
        name: 'Pamukkale',
        nights: 1,
        note: 'The white travertine terraces and the ruined city of Hierapolis on top of them. One night is genuinely enough.',
        onward: { mode: 'Road', time: '3 hr', detail: 'West to Selçuk' },
      },
      {
        name: 'Selçuk and Ephesus',
        nights: 3,
        note: 'One of the best preserved classical cities anywhere. Go at opening or in the last two hours, and pay for the terrace houses.',
        onward: { mode: 'Flight', time: '1 hr 15', detail: 'Izmir home, or back to Istanbul' },
      },
    ],
    shorter: 'On nine nights, Istanbul and Cappadocia with a short coastal stop. Drop Pamukkale first.',
    longer: 'Add a gulet cruise along the Lycian coast, or the Lycian Way if you walk.',
    mistake: 'Booking one morning for the Cappadocia balloons. Flights are grounded for wind far more often than people expect, and a single-morning window means a real chance of missing them entirely. Book the first morning of a three-night stay so you have two more chances if it is cancelled.',
  },
};

export const routeIds = Object.keys(routes);
